import type {
  LandingContent,
  LandingField,
  LandingFieldGroup,
  TextField,
} from "./schema";

/* Keep these rules in step with the first-paint fill script (shellScripts.ts). */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/g;

export function cleanText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return value.replace(CONTROL, "").trim();
}

/** Characters a displayed phone number may contain. */
export const PHONE_CHARS = /^[0-9+(). -]+$/;

/** Returns +1XXXXXXXXXX for a valid US number, otherwise null. */
export function normalizePhone(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const digits = value.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === "1") return `+${digits}`;
  return null;
}
export function phoneHref(display: string): string {
  return `tel:${normalizePhone(display) ?? ""}`;
}
/** "+1-858-333-5005" form for structured data. */
export function phoneSchemaForm(display: string): string {
  const e164 = normalizePhone(display) ?? "";
  return `+1-${e164.slice(2, 5)}-${e164.slice(5, 8)}-${e164.slice(8)}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function getPath(source: unknown, path: string): unknown {
  if (!path) return source;
  let current: unknown = source;
  for (const key of path.split(".")) {
    if (!isPlainObject(current) || !Object.prototype.hasOwnProperty.call(current, key)) return undefined;
    current = current[key];
  }
  return current;
}
function setPath(target: Record<string, unknown>, path: string, value: unknown) {
  const keys = path.split(".");
  let current = target;
  for (const key of keys.slice(0, -1)) {
    if (!isPlainObject(current[key])) current[key] = {};
    current = current[key] as Record<string, unknown>;
  }
  current[keys[keys.length - 1]] = value;
}

function validText(field: TextField, raw: unknown): string | null {
  const value = cleanText(raw);
  if (value === null || value.length < 1 || value.length > field.max) return null;
  if (field.pattern && !field.pattern.test(value)) return null;
  return value;
}

/** Resolves one list item. Returns null when a required subfield is invalid. */
function strictItem(fields: TextField[], raw: unknown): unknown {
  if (fields.length === 1 && fields[0].path === "") return validText(fields[0], raw);
  if (!isPlainObject(raw)) return null;
  const out: Record<string, string> = {};
  for (const field of fields) {
    const value = validText(field, raw[field.path]);
    if (value !== null) out[field.path] = value;
    else if (!field.optional) return null;
  }
  return out;
}
function lenientItem(fields: TextField[], raw: unknown, fallback: unknown): unknown {
  if (fields.length === 1 && fields[0].path === "") return validText(fields[0], raw) ?? fallback;
  const out: Record<string, unknown> = { ...(fallback as Record<string, unknown>) };
  for (const field of fields) {
    const value = validText(field, getPath(raw, field.path));
    if (value !== null) out[field.path] = value;
  }
  return out;
}

function resolveField(field: LandingField, raw: unknown, fallback: unknown): unknown {
  switch (field.type) {
    case "text":
      return validText(field, raw) ?? fallback;
    case "phone": {
      const value = validText({ ...field, type: "text" }, raw);
      return value !== null && normalizePhone(value) && PHONE_CHARS.test(value) ? value : fallback;
    }
    case "list": {
      if (!Array.isArray(raw)) return fallback;
      if (field.allowEmpty && raw.length === 0) return [];
      if (raw.length < field.min || raw.length > field.maxItems) return fallback;
      if (field.min === field.maxItems) {
        const defaults = fallback as unknown[];
        return raw.map((item, i) => lenientItem(field.item, item, defaults[i]));
      }
      const items = raw.map((item) => strictItem(field.item, item));
      return items.every((item) => item !== null) ? items : fallback;
    }
    case "variants": {
      if (!isPlainObject(raw)) return fallback;
      const keys = Object.keys(raw);
      if (keys.length < 1 || keys.length > field.maxItems || !keys.includes(field.requiredKey)) return fallback;
      const out: Record<string, unknown> = {};
      for (const key of keys) {
        const item = field.keyPattern.test(key) ? strictItem(field.item, raw[key]) : null;
        if (item === null) return fallback;
        out[key] = item;
      }
      return out;
    }
  }
}

/** Content is plain JSON data. (structuredClone is missing in Safari before 15.4.) */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * The original headline highlights were checked to fit on one line at every
 * screen width, so they are kept unbroken. Any other text may wrap, which
 * keeps an edited headline from widening the page on phones.
 */
export function keepTogetherHighlights(defaults: LandingContent): string[] {
  return Array.from(new Set(Object.values(defaults.variants).map((v) => v.highlight)));
}

/**
 * The original button label fits on one line on the smallest phones. A longer
 * label may wrap onto two lines instead of pushing the layout wider.
 */
export function isLongLabel(label: string): boolean {
  return label.length > 17;
}

export function forEachField(schema: LandingFieldGroup[], visit: (field: LandingField) => void) {
  for (const group of schema) for (const field of group.fields) visit(field);
}

/** Never throws. Every field falls back to its default when raw is unusable. */
export function resolveContent(
  schema: LandingFieldGroup[],
  defaults: LandingContent,
  raw: unknown,
): LandingContent {
  const out = clone(defaults) as unknown as Record<string, unknown>;
  try {
    forEachField(schema, (field) => {
      // The fallback is read from the copy, so the result never shares lists or objects with the defaults.
      setPath(out, field.path, resolveField(field, getPath(raw, field.path), getPath(out, field.path)));
    });
  } catch {
    return clone(defaults);
  }
  return out as unknown as LandingContent;
}

/** Parses the stored {"v":1,"content":{...}} document. */
export function parseLandingDocument(value: unknown): unknown {
  if (typeof value !== "string") return null;
  try {
    const doc = JSON.parse(value);
    return isPlainObject(doc) && doc.v === 1 && isPlainObject(doc.content) ? doc.content : null;
  } catch {
    return null;
  }
}

/** Max length and fixed list length for a dotted text path, used by the shell. */
export function shellRuleFor(schema: LandingFieldGroup[], path: string): { max: number; fixed?: [string, number] } | null {
  let result: { max: number; fixed?: [string, number] } | null = null;
  forEachField(schema, (field) => {
    if ((field.type === "text" || field.type === "phone") && field.path === path) result = { max: field.max };
    if (field.type === "list" && field.min === field.maxItems && path.startsWith(`${field.path}.`)) {
      const rest = path.slice(field.path.length + 1).split(".");
      const sub = field.item.find((f) => f.path === (rest[1] ?? ""));
      if (/^\d+$/.test(rest[0]) && sub) result = { max: sub.max, fixed: [field.path, field.maxItems] };
    }
    if (field.type === "variants" && path.startsWith("variant.")) {
      const sub = field.item.find((f) => f.path === path.slice(8));
      if (sub) result = { max: sub.max };
    }
  });
  return result;
}
